import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Mail, Phone, MapPin, Clock } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import Logo from "@/components/Logo";
import SocialLinks from "@/components/SocialLinks";
import api from "@/lib/api";

// Coordonnées identiques sur toutes les pages (SEO local + fallback)
const KV_ADDRESS = "Chaussée de Waterloo 242, 1640 Rhode-Saint-Genèse";
const KV_PHONE = "+32 486 52 19 06";
const KV_EMAIL = "contact@kvinternational.be";

export default function Footer() {
  const { t, pick } = useLang();
  const [site, setSite] = useState({});
  useEffect(() => {
    api.get("/site").then((r) => setSite(r.data)).catch(() => {});
  }, []);

  const address = site.address || KV_ADDRESS;
  const phone = site.phone || KV_PHONE;
  const email = site.email || KV_EMAIL;

  return (
    <footer data-testid="site-footer" className="bg-black border-t border-white/10 pt-20 pb-8">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid md:grid-cols-4 gap-12">
        <div>
          <div className="mb-6">
            <Logo asLink={false} withText={false} size="w-20 h-20" />
          </div>
          <p className="text-white/60 text-sm leading-relaxed">{t.footer.tagline}</p>
          <div className="mt-6">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#C8A75A]/80 mb-3">Nous suivre</p>
            <SocialLinks variant="footer" />
          </div>
        </div>

        <div>
          <h4 className="text-xs uppercase tracking-[0.2em] text-[#C8A75A] font-semibold mb-5">{t.footer.nav}</h4>
          <ul className="space-y-3 text-sm">
            <li><Link to="/stock" className="text-white/70 hover:text-white">{t.nav.stock}</Link></li>
            <li><Link to="/services" className="text-white/70 hover:text-white">{t.nav.services}</Link></li>
            <li><Link to="/simulator" className="text-white/70 hover:text-white">{t.nav.simulator}</Link></li>
            <li><Link to="/about" className="text-white/70 hover:text-white">{t.nav.about}</Link></li>
            <li><Link to="/contact" className="text-white/70 hover:text-white">{t.nav.contact}</Link></li>
            <li>
              <Link
                to="/login"
                data-testid="footer-login-link"
                className="text-[#C8A75A]/80 hover:text-[#C8A75A] tracking-wide uppercase text-xs"
              >
                {t.nav.login}
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="text-xs uppercase tracking-[0.2em] text-[#C8A75A] font-semibold mb-5">{t.footer.contact}</h4>
          <ul data-testid="footer-contact-block" className="space-y-3 text-sm text-white/70">
            <li className="flex gap-3"><MapPin className="w-4 h-4 mt-0.5 text-[#C8A75A] flex-shrink-0" /><span itemProp="address">{address}</span></li>
            <li className="flex gap-3"><Phone className="w-4 h-4 mt-0.5 text-[#C8A75A] flex-shrink-0" /><a href={`tel:${phone.replace(/\s+/g, "")}`} itemProp="telephone">{phone}</a></li>
            <li className="flex gap-3"><Mail className="w-4 h-4 mt-0.5 text-[#C8A75A] flex-shrink-0" /><a href={`mailto:${email}`} itemProp="email">{email}</a></li>
            {site.hours && <li className="flex gap-3"><Clock className="w-4 h-4 mt-0.5 text-[#C8A75A] flex-shrink-0" /><span>{pick(site.hours)}</span></li>}
            {!site.hours && <li className="flex gap-3"><Clock className="w-4 h-4 mt-0.5 text-[#C8A75A] flex-shrink-0" /><span>Sur rendez-vous · 7j/7</span></li>}
          </ul>
        </div>

        <div>
          <h4 className="text-xs uppercase tracking-[0.2em] text-[#C8A75A] font-semibold mb-5">{t.footer.legal}</h4>
          <p className="text-xs text-white/50 leading-relaxed">
            KV International SRL<br/>
            {address}<br/>
            {email}
          </p>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-3 text-xs text-white/40">
        <p>© {new Date().getFullYear()} KV International — {t.footer.rights}</p>
        <p className="tracking-widest uppercase">Made with care</p>
      </div>
    </footer>
  );
}
